// Shared active-format selector: the <select>, both status pills, and
// (optionally) the TextPatternInput for OCR mode. Both /scan and /capture
// render this identically — the difference lives in the useActiveFormat
// hook + the individual host page's `showTextPatternInput` prop.

import {
  fiducialStatusColor,
  fiducialStatusLabel,
  isFiducialFormat,
  type FiducialStatus,
} from '../lib/fiducialDecoder';
import {
  tesseractStatusColor,
  tesseractStatusLabel,
  type TesseractStatus,
} from '../lib/ensureTesseract';
import { isActiveFormat, type ActiveFormat } from '../types';
import { TextPatternInput } from './TextPatternInput';

interface ActiveFormatPickerProps {
  activeFormat: ActiveFormat;
  onFormatChange: (f: ActiveFormat) => void;
  fiducialStatus: FiducialStatus;
  tesseractStatus: TesseractStatus;
  textPattern: string;
  onTextPatternChange: (s: string) => void;
  /** id of the <select> — different between /scan and /capture surfaces. */
  selectId: string;
  /** When false, hides the regex input row (e.g. capture surface doesn't wire OCR yet). */
  showTextPatternInput?: boolean;
  /** When true, shows the "OCR runs on /scan" notice under the picker. */
  textPatternNotice?: string;
  disabled?: boolean;
}

export function ActiveFormatPicker({
  activeFormat,
  onFormatChange,
  fiducialStatus,
  tesseractStatus,
  textPattern,
  onTextPatternChange,
  selectId,
  showTextPatternInput = true,
  textPatternNotice,
  disabled = false,
}: ActiveFormatPickerProps) {
  return (
    <>
      <label className="flex items-center gap-2 text-sm text-gray-300" htmlFor={selectId}>
        <span>Detect</span>
        <select
          id={selectId}
          data-testid="active-format-picker"
          value={activeFormat}
          onChange={(e) => {
            const next = e.target.value;
            if (isActiveFormat(next)) onFormatChange(next);
          }}
          disabled={disabled}
          className="rounded border border-gray-600 bg-gray-800 px-2 py-1"
        >
          <option value="qr">QR</option>
          <option value="aruco_4x4_1000">ArUco 4×4 (1000)</option>
          <option value="text">Text (OCR)</option>
        </select>
        {isFiducialFormat(activeFormat) && (
          <span
            className="text-xs"
            role="status"
            aria-live="polite"
            data-testid="fiducial-status"
            style={{ color: fiducialStatusColor(fiducialStatus) }}
          >
            {fiducialStatusLabel(fiducialStatus)}
          </span>
        )}
        {activeFormat === 'text' && (
          <span
            className="text-xs"
            role="status"
            aria-live="polite"
            data-testid="tesseract-status"
            style={{ color: tesseractStatusColor(tesseractStatus) }}
          >
            {tesseractStatusLabel(tesseractStatus)}
          </span>
        )}
      </label>
      {activeFormat === 'text' && showTextPatternInput && (
        <TextPatternInput value={textPattern} onChange={onTextPatternChange} />
      )}
      {activeFormat === 'text' && !showTextPatternInput && textPatternNotice && (
        <p className="text-xs text-gray-500">{textPatternNotice}</p>
      )}
    </>
  );
}
